import React, { useCallback, useEffect, useState } from "react";
import { errorMessage } from "../../services";
import { getCourierRates, shipOrder } from "../../services/orders";
import { money } from "../../utils/format";
import Loader, { Spinner } from "../../components/Loader";

// Book a courier for one order through the store's Shiprocket account: pick a courier from the live rates, check
// the parcel size, book. Shiprocket assigns the AWB, schedules pickup and makes the label.
export default function ShipModal({ siteId, order, onClose, onBooked, notify }) {
  const [couriers, setCouriers] = useState(null);
  const [parcel, setParcel] = useState({ weight: "", length: "", breadth: "", height: "" });
  const [courierId, setCourierId] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadRates = useCallback(async (weight) => {
    setCouriers(null);
    setError("");
    try {
      const { data } = await getCourierRates(siteId, order.id, weight ? { weight } : undefined);
      setCouriers(data.couriers);
      setParcel((p) => ({ weight: p.weight || data.parcel.weight, length: p.length || data.parcel.length, breadth: p.breadth || data.parcel.breadth, height: p.height || data.parcel.height }));
      const pick = data.couriers.find((c) => c.recommended) || data.couriers[0];
      setCourierId(pick ? pick.id : null);
    } catch (err) {
      setError(errorMessage(err));
      setCouriers([]);
    }
  }, [siteId, order.id]);

  useEffect(() => { loadRates(); }, [loadRates]);

  const set = (key) => (e) => setParcel((p) => ({ ...p, [key]: e.target.value }));

  const book = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await shipOrder(siteId, order.id, { courierId, weight: Number(parcel.weight), length: Number(parcel.length), breadth: Number(parcel.breadth), height: Number(parcel.height) });
      notify(data.message);
      onBooked(data.order);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <section className="mo-modal ship-modal" role="dialog" aria-modal="true" aria-label={`Ship order ${order.id}`}>
        <div className="mo-modal-hero tone-blue">
          <div>
            <small>SHIP WITH COURIER</small>
            <h2>Order #{order.id}</h2>
            <span className="mo-hero-date">To {order.customer.name} · {order.customer.city} {order.customer.pincode} · {order.paymentStatus === "paid" ? "Prepaid" : `COD ${money(order.total)}`}</span>
          </div>
          <button type="button" className="mo-modal-close" onClick={onClose} disabled={busy} aria-label="Close">✕</button>
        </div>
        <div className="mo-modal-body">
          <fieldset className="mo-edit-section">
            <legend><i aria-hidden="true">📦</i> Parcel</legend>
            <div className="ship-parcel">
              <label>Weight (kg)<input type="number" step="0.05" min="0.05" value={parcel.weight} onChange={set("weight")} onBlur={() => parcel.weight && loadRates(parcel.weight)} /></label>
              <label>L (cm)<input type="number" min="1" value={parcel.length} onChange={set("length")} /></label>
              <label>B (cm)<input type="number" min="1" value={parcel.breadth} onChange={set("breadth")} /></label>
              <label>H (cm)<input type="number" min="1" value={parcel.height} onChange={set("height")} /></label>
            </div>
            <small className="ship-hint">Prices update when you change the weight.</small>
          </fieldset>

          <h3>Choose a courier {couriers && couriers.length > 0 && <span>{couriers.length}</span>}</h3>
          {error && <div className="admin-error">{error}</div>}
          {!couriers && <Loader label="Getting live courier prices" />}
          {couriers && !couriers.length && !error && <div className="admin-empty">No courier delivers to this pincode with these settings.</div>}
          <div className="ship-couriers" role="radiogroup" aria-label="Courier">
            {(couriers || []).map((c) => (
              <label key={c.id} className={`ship-courier ${courierId === c.id ? "is-on" : ""}`}>
                <input type="radio" name="courier" checked={courierId === c.id} onChange={() => setCourierId(c.id)} />
                <span className="ship-courier-name"><b>{c.name}</b><small>{c.days ? `${c.days} day${c.days === 1 ? "" : "s"}` : c.etd || "Delivery time not given"}{c.rating ? ` · ★ ${c.rating.toFixed(1)}` : ""}</small></span>
                {c.recommended && <em>Recommended</em>}
                <strong>{money(c.rate)}</strong>
              </label>
            ))}
          </div>
        </div>
        <div className="mo-modal-actions">
          <button type="button" className="mo-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="mo-primary" onClick={book} disabled={busy || !courierId}>{busy ? <><Spinner /> Booking</> : "Book courier"}</button>
        </div>
      </section>
    </div>
  );
}
